"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { stripEndToken } from "@lib/agent/conversation/endToken";
import { goodbyeKeywordsForLocale } from "@lib/agent/conversation/goodbyeKeywords";
import { randomId } from "@lib/randomId";
import { parseLocalizedTitles } from "@lib/localizedTitles";
import { DEFAULT_TARGET_LOCALE } from "@lib/courseConstants";
import type { AgentLessonContent } from "@lib/agent/lessonSnapshot";
import { applyLessonContentPatch, mergeLessonContextInput } from "@lib/agent/lessonContextState";
import type { AgentPageContext } from "@lib/agent/pageContext";
import type { ChatAboutFeedbackDetail } from "@lib/agent/chatAboutFeedback";
import type {
  AgentEvent,
  ConversationDebrief,
  ConversationDifficulty,
  ConversationPersona,
  ConversationTranscriptRow,
  LessonContext,
  LessonContextInput,
  OverlayState,
} from "./types";
import { lessonTypeToPhase } from "./types";
import { useInputCapture } from "./useInputCapture";
import { useAgentStream } from "./useAgentStream";
import type {
  AgentPromptPayload,
  FireOptions,
  PromptHistoryEntry,
  PromptTrigger,
} from "./useAgentStream";
import { useEventQueue } from "./useEventQueue";
import type { DebugEvent } from "./useEventQueue";

// Re-export types that consumers import from this module
export type { AgentPromptPayload, PromptTrigger, PromptHistoryEntry };
export type { DebugEvent };

export type DebriefRequestParams = {
  sessionId: number;
  persona: ConversationPersona;
  scenarioTitle: string;
  difficulty: ConversationDifficulty;
  transcript: ConversationTranscriptRow[];
  endedEarly: boolean;
};

const DEBRIEF_ERROR_KEY = "agent.conversation.error.debrief";

export type DebugRefs = {
  lastFiredAt: React.MutableRefObject<number | null>;
  lastActivityAt: React.MutableRefObject<number>;
  performance: React.MutableRefObject<{ correct: number; incorrect: number; streak: number }>;
  pendingDelayUntil: React.MutableRefObject<number | null>;
  events: React.MutableRefObject<DebugEvent[]>;
  lessonContext: React.MutableRefObject<LessonContextInput | null>;
  promptHistory: React.MutableRefObject<PromptHistoryEntry[]>;
  lastStreamError: React.MutableRefObject<string | null>;
};

const TUTOR_MODE = { type: "tutor" as const };

const INITIAL_OVERLAY_STATE: OverlayState = {
  mode: TUTOR_MODE,
  messages: [],
  isOpen: false,
  nudgeAt: 0,
};

function isNonTutorMode(mode: OverlayState["mode"]): boolean {
  return mode.type !== "tutor";
}

type UseAgentOverlayReturn = {
  state: OverlayState;
  dispatchEvent: (event: AgentEvent) => void;
  sendUserMessage: (text: string) => void;
  startConversation: () => void;
  selectScenario: (
    persona: ConversationPersona,
    scenarioTitle: string,
    sessionId: number,
    difficulty: ConversationDifficulty,
  ) => void;
  sendConversationTurn: (text: string) => void;
  endConversation: (endedEarly: boolean) => void;
  requestDebrief: (params: DebriefRequestParams) => Promise<ConversationDebrief | null>;
  returnToTutor: () => void;
  retryDebrief: () => void;
  setVoiceFallback: () => void;
  setLessonContext: (ctx: LessonContextInput | null) => void;
  setLessonContent: (content: AgentLessonContent | null) => void;
  setPageContext: (ctx: AgentPageContext | null) => void;
  updatePageData: (patch: Record<string, unknown>) => void;
  dismiss: () => void;
  open: () => void;
  debugRefs: DebugRefs;
  getPromptPayload: () => AgentPromptPayload;
};

function normalizeForGoodbyeMatch(text: string): string {
  return text.trim().toLowerCase();
}

function matchesGoodbyeKeyword(text: string, targetLocale: string): boolean {
  const normalized = normalizeForGoodbyeMatch(text);
  return goodbyeKeywordsForLocale(targetLocale).some(
    (keyword) => normalized === keyword.toLowerCase(),
  );
}

function buildTranscriptFromMessages(
  messages: OverlayState["messages"],
): ConversationTranscriptRow[] {
  return messages
    .filter((m) => !m.isStreaming && (m.role === "user" || m.role === "persona"))
    .map((m) => ({
      role: m.role === "user" ? ("learner" as const) : ("persona" as const),
      text: m.text,
      timestamp: new Date().toISOString(),
    }));
}

export function useAgentOverlay(): UseAgentOverlayReturn {
  const [state, setState] = useState<OverlayState>(INITIAL_OVERLAY_STATE);
  const modeRef = useRef(state.mode);
  const endedEarlyRef = useRef(false);
  const lastDebriefParamsRef = useRef<DebriefRequestParams | null>(null);
  const requestDebriefRef = useRef<
    (params: DebriefRequestParams) => Promise<ConversationDebrief | null>
  >(async () => null);

  useEffect(() => {
    modeRef.current = state.mode;
  }, [state.mode]);

  const lessonContextRef = useRef<LessonContextInput | null>(null);
  const pendingContentRef = useRef<AgentLessonContent | null>(null);
  const pageContextRef = useRef<AgentPageContext | null>(null);
  const eventsRef = useRef<AgentEvent[]>([]);
  const performanceRef = useRef({ correct: 0, incorrect: 0, streak: 0 });
  const lastFiredRef = useRef<number | null>(null);
  const pendingDelayUntilRef = useRef<number | null>(null);
  const promptHistoryRef = useRef<PromptHistoryEntry[]>([]);
  const lastStreamErrorRef = useRef<string | null>(null);

  // Stable ref to break the circular dep: useEventQueue reads fireRef.current at dispatch time
  const fireRef = useRef<
    (userMessage?: string, trigger?: PromptTrigger, options?: FireOptions) => void
  >(() => {});

  const buildLessonContext = useCallback((): LessonContext | null => {
    const ctx = lessonContextRef.current;
    const lesson = ctx?.lesson;
    if (!lesson) return null;
    return {
      lesson,
      content: ctx?.content ?? null,
      lessonId: String(lesson.id),
      lessonName: parseLocalizedTitles(lesson.titles).target || lesson.name || String(lesson.id),
      phase: ctx?.phase ?? lessonTypeToPhase(lesson.lessonType),
      recentEvents: eventsRef.current.slice(-10),
      performance: { ...performanceRef.current },
      targetLocale: ctx?.targetLocale,
      nativeLocale: ctx?.nativeLocale,
    };
  }, []);

  const buildPayload = useCallback(
    () => ({
      pageContext: pageContextRef.current,
      lessonContext: buildLessonContext(),
    }),
    [buildLessonContext],
  );

  const { fire, cancelStream, resetStreamHistory, lastPromptPayloadRef, streamConversationTurn } =
    useAgentStream({
      setState,
      buildPayload,
      lastFiredRef,
      pendingDelayUntilRef,
      lastStreamErrorRef,
      promptHistoryRef,
    });

  useEffect(() => {
    fireRef.current = fire;
  }, [fire]);

  const {
    dispatchEvent: rawDispatchEvent,
    resetEventQueue,
    debugEventsRef,
  } = useEventQueue({
    fireRef,
    lastFiredRef,
    pendingDelayUntilRef,
    lessonContextRef,
    eventsRef,
    performanceRef,
  });

  const dispatchEvent = useCallback(
    (event: AgentEvent) => {
      if (isNonTutorMode(modeRef.current)) return;
      rawDispatchEvent(event);
    },
    [rawDispatchEvent],
  );

  const resetLessonSession = useCallback(() => {
    eventsRef.current = [];
    performanceRef.current = { correct: 0, incorrect: 0, streak: 0 };
    resetEventQueue();
    resetStreamHistory();
  }, [resetEventQueue, resetStreamHistory]);

  const setLessonContext = useCallback(
    (ctx: LessonContextInput | null) => {
      if (ctx === null) {
        lessonContextRef.current = null;
        pendingContentRef.current = null;
        resetLessonSession();
        return;
      }
      const merged = mergeLessonContextInput(
        lessonContextRef.current,
        pendingContentRef.current,
        ctx,
      );
      lessonContextRef.current = merged.ctx;
      pendingContentRef.current = merged.pendingContent;
    },
    [resetLessonSession],
  );

  const setLessonContent = useCallback((content: AgentLessonContent | null) => {
    if (content === null) {
      if (lessonContextRef.current) {
        lessonContextRef.current = { ...lessonContextRef.current, content: null };
      }
      pendingContentRef.current = null;
      return;
    }
    const patched = applyLessonContentPatch(
      lessonContextRef.current,
      pendingContentRef.current,
      content,
    );
    lessonContextRef.current = patched.ctx;
    pendingContentRef.current = patched.pendingContent;
  }, []);

  const setPageContext = useCallback((ctx: AgentPageContext | null) => {
    pageContextRef.current = ctx;
  }, []);

  /** Merge client-side state (e.g. selected radar spoke) into the page data. */
  const updatePageData = useCallback((patch: Record<string, unknown>) => {
    const current = pageContextRef.current;
    if (!current) return;
    pageContextRef.current = { ...current, data: { ...current.data, ...patch } };
  }, []);

  const getPromptPayload = useCallback(
    (): AgentPromptPayload => ({
      ...buildPayload(),
      userMessage: lastPromptPayloadRef.current?.userMessage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildPayload],
  );

  const sendUserMessage = useCallback(
    (text: string) => {
      if (isNonTutorMode(modeRef.current)) return;
      const trimmed = text.trim();
      if (!trimmed) return;
      setState((prev) => ({
        ...prev,
        isOpen: true,
        messages: [
          ...prev.messages,
          { id: randomId(), text: trimmed, isStreaming: false, role: "user" },
        ],
      }));
      fire(trimmed, "user_message");
    },
    [fire],
  );

  const startConversation = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: true,
      mode: {
        type: "conversation",
        subphase: "picker",
        scenarioTitle: "",
        turnCount: 0,
        difficulty: "standard",
        voiceFallback: false,
      },
    }));
  }, []);

  const selectScenario = useCallback(
    (
      persona: ConversationPersona,
      scenarioTitle: string,
      sessionId: number,
      difficulty: ConversationDifficulty,
    ) => {
      setState((prev) => ({
        ...prev,
        isOpen: true,
        mode: {
          type: "conversation",
          subphase: "active",
          persona,
          scenarioTitle,
          turnCount: 0,
          sessionId,
          difficulty,
          voiceFallback: false,
        },
        messages: [
          ...prev.messages,
          {
            id: randomId(),
            text: persona.openingLine,
            isStreaming: false,
            role: "persona",
          },
        ],
      }));
    },
    [],
  );

  const endConversation = useCallback((endedEarly: boolean) => {
    endedEarlyRef.current = endedEarly;
    setState((prev) => {
      if (prev.mode.type !== "conversation" || prev.mode.subphase !== "active") {
        return prev;
      }
      const { sessionId, persona, scenarioTitle, difficulty } = prev.mode;
      if (sessionId == null || !persona) return prev;

      const transcript = buildTranscriptFromMessages(prev.messages);
      const debriefParams: DebriefRequestParams = {
        sessionId,
        persona,
        scenarioTitle,
        difficulty,
        transcript,
        endedEarly,
      };
      lastDebriefParamsRef.current = debriefParams;
      queueMicrotask(() => {
        void requestDebriefRef.current(debriefParams);
      });

      return {
        ...prev,
        mode: {
          type: "debrief",
          sessionId,
          analyzing: true,
          transcriptCollapsed: true,
        },
      };
    });
  }, []);

  const sendConversationTurn = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const targetLocale = lessonContextRef.current?.targetLocale ?? DEFAULT_TARGET_LOCALE;

      setState((prev) => {
        if (prev.mode.type !== "conversation" || prev.mode.subphase !== "active") {
          return prev;
        }
        if (!prev.mode.persona) return prev;

        const userMessage = {
          id: randomId(),
          text: trimmed,
          isStreaming: false,
          role: "user" as const,
        };

        if (matchesGoodbyeKeyword(trimmed, targetLocale)) {
          queueMicrotask(() => endConversation(false));
          return {
            ...prev,
            isOpen: true,
            messages: [...prev.messages, userMessage],
          };
        }

        const transcript = buildTranscriptFromMessages([...prev.messages, userMessage]);
        const { persona, sessionId, difficulty } = prev.mode;

        streamConversationTurn(
          {
            sessionId,
            persona,
            difficulty,
            transcript,
            learnerMessage: trimmed,
          },
          {
            onComplete: (fullText) => {
              const { display, ended } = stripEndToken(fullText);
              setState((current) => {
                if (current.mode.type !== "conversation") return current;
                const messages = [...current.messages];
                for (let i = messages.length - 1; i >= 0; i -= 1) {
                  const message = messages[i];
                  if (message.role === "persona" && !message.isStreaming) {
                    messages[i] = { ...message, text: display };
                    break;
                  }
                }
                return {
                  ...current,
                  messages,
                  mode: { ...current.mode, turnCount: current.mode.turnCount + 1 },
                };
              });
              // Plan 06: stripEndToken(display) before fetchTutorSpeechBuffer for persona TTS
              if (ended) {
                endConversation(false);
              }
            },
          },
        );

        return {
          ...prev,
          isOpen: true,
          messages: [...prev.messages, userMessage],
        };
      });
    },
    [streamConversationTurn, endConversation],
  );

  const requestDebrief = useCallback(
    async (params: DebriefRequestParams): Promise<ConversationDebrief | null> => {
      setState((prev) => {
        if (prev.mode.type !== "debrief") return prev;
        return {
          ...prev,
          mode: { ...prev.mode, analyzing: true, error: undefined },
        };
      });

      try {
        const res = await fetch("/api/agent/conversation/debrief", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(params),
        });
        if (!res.ok) {
          setState((prev) => {
            if (prev.mode.type !== "debrief") return prev;
            return {
              ...prev,
              mode: { ...prev.mode, analyzing: false, error: DEBRIEF_ERROR_KEY },
            };
          });
          return null;
        }
        const debrief = (await res.json()) as ConversationDebrief;
        setState((prev) => {
          if (prev.mode.type !== "debrief") return prev;
          return {
            ...prev,
            mode: { ...prev.mode, analyzing: false, debrief, error: undefined },
          };
        });
        return debrief;
      } catch {
        setState((prev) => {
          if (prev.mode.type !== "debrief") return prev;
          return {
            ...prev,
            mode: { ...prev.mode, analyzing: false, error: DEBRIEF_ERROR_KEY },
          };
        });
        return null;
      }
    },
    [],
  );

  useEffect(() => {
    requestDebriefRef.current = requestDebrief;
  }, [requestDebrief]);

  const returnToTutor = useCallback(() => {
    cancelStream();
    endedEarlyRef.current = false;
    setState((prev) => ({
      ...prev,
      mode: TUTOR_MODE,
      messages: [],
    }));
  }, [cancelStream]);

  const retryDebrief = useCallback(() => {
    const params = lastDebriefParamsRef.current;
    if (params) void requestDebrief(params);
  }, [requestDebrief]);

  const setVoiceFallback = useCallback(() => {
    setState((prev) => {
      if (prev.mode.type !== "conversation") return prev;
      return { ...prev, mode: { ...prev.mode, voiceFallback: true } };
    });
  }, []);

  useEffect(() => {
    function handleChatAboutFeedback(event: Event) {
      const detail = (event as CustomEvent<ChatAboutFeedbackDetail>).detail ?? {};
      if (detail.message) sendUserMessage(detail.message);
    }

    window.addEventListener("convo:chat-about-feedback", handleChatAboutFeedback);
    return () => {
      window.removeEventListener("convo:chat-about-feedback", handleChatAboutFeedback);
    };
  }, [sendUserMessage]);

  const dismiss = useCallback(() => {
    cancelStream();
    setState((prev) => ({ ...prev, isOpen: false }));
  }, [cancelStream]);

  const open = useCallback(() => setState((prev) => ({ ...prev, isOpen: true })), []);

  const handleStall = useCallback(() => {
    if (isNonTutorMode(modeRef.current)) return;
    if (!lessonContextRef.current) return;
    dispatchEvent({ type: "stall", payload: "no interaction", relativeMs: 0 });
  }, [dispatchEvent]);

  const { lastActivityRef } = useInputCapture(handleStall, true);

  // All members are stable refs - memoize the wrapper object to avoid re-creating it every render
  const debugRefs: DebugRefs = useMemo(
    () => ({
      lastFiredAt: lastFiredRef,
      lastActivityAt: lastActivityRef,
      performance: performanceRef,
      pendingDelayUntil: pendingDelayUntilRef,
      events: debugEventsRef,
      lessonContext: lessonContextRef,
      promptHistory: promptHistoryRef,
      lastStreamError: lastStreamErrorRef,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return {
    state,
    dispatchEvent,
    sendUserMessage,
    startConversation,
    selectScenario,
    sendConversationTurn,
    endConversation,
    requestDebrief,
    returnToTutor,
    retryDebrief,
    setVoiceFallback,
    setLessonContext,
    setLessonContent,
    setPageContext,
    updatePageData,
    dismiss,
    open,
    debugRefs,
    getPromptPayload,
  };
}
